// React dependencies
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

// DWT hooks
import useMediaQuery from '../hooks/useMediaQuery';

import { facebookIcon, goodreadsIcon, instagramIcon, linkedInIcon, pinterestIcon2, snapchatIcon, tiktokIcon, twitterIcon, youtubeIcon, blueskyIcon, threadsIcon } from '../img/icons';

/**
 * Nav
 *
 * A component to handle the site footer
 * @see global-options.php to see how we are handling menu data in the global DP object
 *
 */
const Footer = () => {
  const [ footerMenuData, setFooterMenuData ] = useState( [] );
  const [ itemID ] = useState( null );
  const isDesktop = useMediaQuery( '( min-width: 1024px )' );

  useEffect( () => {
    setFooterMenuData( window.DP.footer_menu_data );
  }, [] );

  const footerMenu = footerMenuData?.map( ( item ) => item.menu_item_parent === '0' ? item : null ).filter( ( fitleredItem ) => fitleredItem !== null );
  footerMenu.filter( ( item, index ) => index <= 7 ? item : null );

  const footerLogo = window.DP.footer_logo;
  const footerMobileLogo = window.DP.footer_logo_mobile;
  const privacyPolicyLink = window.DP.privacy_policy_link;
  const termsOfUseLink = window.DP.terms_of_use_link;
  const copyrightText = window.DP.copyright_text;
  const socialMediaLinks = window.DP.social_media_links;
  const caPrivacyPolicy = window.DP.ca_privacy_policy;
  const doNotSellMyPersonalInfo = window.DP.do_not_sell_my_personal_info;
  const affiliateProgramDisclosure = window.DP.affiliate_program_disclosure;

  const icons = {
    tiktok_link: {
      icon: tiktokIcon,
      name: 'TikTok'
    },
    instagram_link: {
      icon: instagramIcon,
      name: 'Instagram'
    },
    facebook_link: {
      icon: facebookIcon,
      name: 'Facebook'
    },
    twitter_link: {
      icon: twitterIcon,
      name: 'X'
    },
    linkedin_link: {
      icon: linkedInIcon,
      name: 'Linkedin'
    },
    snapchat_link: {
      icon: snapchatIcon,
      name: 'Snapchat'
    },
    pinterest_link: {
      icon: pinterestIcon2,
      name: 'Pinterest'
    },
    youtube_link: {
      icon: youtubeIcon,
      name: 'YouTube'
    },
    goodreads_link: {
      icon: goodreadsIcon,
      name: 'Goodreads'
    },
    bluesky_link: {
      icon: blueskyIcon,
      name: 'Bluesky'
    },
    threads_link: {
      icon: threadsIcon,
      name: 'Threads'
    }
  };

  const activeIcons = [];

  for ( const platform in socialMediaLinks ) {
    if ( socialMediaLinks.hasOwnProperty( platform ) ) {
      const link = socialMediaLinks[platform];
      if ( link !== '' ) {
        activeIcons.push({ icon: icons[platform].icon, name: icons[platform].name, link });
      }
    }
  }

  return (
    <div className={`flex flex-col justify-center container ${!isDesktop ? 'is-mobile-footer' : ''}`}>
      {( footerLogo?.url || footerMobileLogo?.url ) && (
        <div className='flex justify-center mb-20'>
          <a href='/' data-title='home'>
            <img className='h-[50px]' src={ isDesktop ? footerLogo?.url : footerMobileLogo?.url } alt={ isDesktop ? footerLogo?.alt : footerMobileLogo?.url } />
          </a>
        </div>
      )}

      {activeIcons.length > 0 && (
        <div className="footer-social-media-logos flex justify-center gap-15 mb-20">
          {activeIcons.map( ( item, index ) => (
            <a key={index} href={item.link} target="_blank" rel="noopener noreferrer" aria-label={'Follow on ' + item.name} title={item.name}>
              {item.icon}
            </a>
          ) )}
        </div>
      )}
      {footerMenu.length > 0 && (
        <ul className="footer-items list-none flex justify-center px-0 flex-wrap gap-x-20 gap-y-16 text-center flex-col lg:flex-row max-w-[700px] mx-auto mb-20">
          { footerMenu?.map( ( item, index ) => (
            <li
              key={ item.ID }
              className={`footer-item mb-0 ${itemID === item.ID ? 'is-active' : ''} ${item.url ? 'has-link' : 'has-button'}`}
              data-item-index={ index }
              aria-expanded={ !!itemID }
              aria-label={ item.title }
              role='menuitem'
            >
              { item.url && (
                <a
                  className='footer-item__link whitespace-nowrap no-underline'
                  href={ item.url }
                  target={ item.target ? '_blank' : null }
                  rel={ item.target ? 'noopener noreferrer' : null }
                  role='link'
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: item.title
                    }}
                  ></span>
                </a>
              ) }
            </li>
          ) )}
        </ul>
      )}

      <div className="flex flex-col justify-center items-center gap-10">
        <div>
          <a href={termsOfUseLink} target="_blank" rel="noopener noreferrer">Terms of Use</a><span className="mx-10">|</span><a href={privacyPolicyLink} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        </div>
        { ( caPrivacyPolicy || doNotSellMyPersonalInfo ) && (
          <div className="flex flex-col sm:flex-row justify-center items-center gap-10">
            { caPrivacyPolicy && (
              <a href={caPrivacyPolicy} target="_blank" rel="noopener noreferrer">CA Privacy Policy</a>
            )}
            { ( caPrivacyPolicy && doNotSellMyPersonalInfo ) && (
              <span className="hidden sm:block">|</span>
            )}
            { doNotSellMyPersonalInfo && (
              <a href={doNotSellMyPersonalInfo} target="_blank" rel="noopener noreferrer">Do Not Sell My Personal Info</a>
            )}
          </div>
        )}
        { affiliateProgramDisclosure && (
          <div>
            <a href={affiliateProgramDisclosure} target="_blank" rel="noopener noreferrer">Affiliate Program Disclosure</a>
          </div>
        )}
        <div className="copyright mx-15">
          <p>&copy; {new Date().getFullYear()} {copyrightText}</p>
        </div>
      </div>
    </div>
  );
};

const renderFooter = () => {
  const footer = document.querySelector( 'footer' );
  if ( !footer ) return;

  const root = createRoot( footer );
  root.render( <Footer /> );
};

export default renderFooter;
